import React, { useEffect, useState } from "react";
import { Star, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";

export default function ReviewsSection({ school }) {
  const { user } = useAuth();
  const [items, setItems] = useState(school.reviews || []);
  const [avg, setAvg] = useState(school.avg_rating || 0);
  const [count, setCount] = useState(school.review_count || 0);
  const [form, setForm] = useState({ rating: 5, title: "", body: "", role: "veli" });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const reload = () => {
    api.get(`${V1}/reviews/school/${school.id}`).then((r) => {
      setItems(r.data.items || []);
      setAvg(r.data.avg_rating || 0);
      setCount(r.data.count || 0);
    });
  };

  const submit = async () => {
    if (!form.body) return toast.error("Yorum metni gerekli");
    setBusy(true);
    try {
      await api.post(`${V1}/reviews`, { school_id: school.id, ...form });
      toast.success("Yorumunuz alındı, moderasyon sonrası yayınlanacak.");
      setForm({ rating: 5, title: "", body: "", role: "veli" });
      setShowForm(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gönderilemedi");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Veli & Öğrenci Yorumları</h2>
          {count > 0 ? (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < Math.round(avg) ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                ))}
              </div>
              <span className="font-mono font-bold">{avg.toFixed(1)}</span>
              <span className="text-sm text-muted-foreground">({count} yorum)</span>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground mt-2">Henüz yorum yok. İlk yorumu siz yapın!</div>
          )}
        </div>
        {user?.role === "parent" || user?.role === "student" ? (
          <Button onClick={() => setShowForm(!showForm)} variant={showForm ? "outline" : "default"} data-testid="write-review-btn">
            {showForm ? "İptal" : "Yorum Yaz"}
          </Button>
        ) : !user ? (
          <Button asChild variant="outline"><Link to="/giris">Yorum yazmak için giriş yap</Link></Button>
        ) : null}
      </div>

      {showForm && (
        <div className="rounded-xl border border-border bg-card p-6 mb-6" data-testid="review-form">
          <div className="grid gap-4">
            <div>
              <Label>Puanınız</Label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setForm({ ...form, rating: n })} type="button" data-testid={`rating-${n}`}>
                    <Star className={`h-6 w-6 ${n <= form.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div><Label>Başlık (opsiyonel)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="review-title" /></div>
            <div><Label>Yorumunuz</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Deneyiminizi paylaşın…" data-testid="review-body" /></div>
            <Button onClick={submit} disabled={busy} data-testid="submit-review-btn">
              {busy ? "Gönderiliyor…" : "Yorumu Gönder"}
            </Button>
            <div className="text-xs text-muted-foreground">Yorumunuz moderasyon sonrasında yayınlanır. Doğrulanmış rozet, bu okula başvurmuş kullanıcılara verilir.</div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {items.length === 0 && !showForm && (
          <div className="p-8 rounded-lg border border-dashed border-border text-center text-muted-foreground">
            Bu okul için henüz yorum yayınlanmadı.
          </div>
        )}
        {items.map((r) => (
          <div key={r.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className={`h-4 w-4 ${i < r.rating ? "fill-accent text-accent" : "text-muted-foreground"}`} />
                ))}
                {r.verified && <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-success bg-success/10 px-2 py-0.5 rounded"><ShieldCheck className="h-3 w-3" /> Doğrulanmış</span>}
              </div>
              <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("tr-TR")}</div>
            </div>
            {r.title && <div className="font-display font-bold mb-1">{r.title}</div>}
            <div className="text-sm text-foreground/90 leading-relaxed">{r.body}</div>
            <div className="text-xs text-muted-foreground mt-3">— {r.author_name}, {r.role}</div>
            {r.admin_reply && (
              <div className="mt-3 p-3 rounded bg-muted text-sm">
                <div className="overline text-accent mb-1">Okul Yanıtı</div>
                {r.admin_reply}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
